import { EnvironmentProviders, inject, Injector, makeEnvironmentProviders, Signal } from '@angular/core'
import { toSignal } from '@angular/core/rxjs-interop'
import { provideTanStackQuery, QueryFeatures } from '@tanstack/angular-query-experimental'
import {
  DefaultError,
  EnsureQueryDataOptions,
  FetchInfiniteQueryOptions,
  FetchQueryOptions,
  InfiniteData,
  InfiniteQueryObserver,
  InfiniteQueryObserverOptions,
  InfiniteQueryObserverResult,
  MutateOptions,
  MutationObserver,
  MutationObserverOptions,
  MutationObserverResult,
  notifyManager,
  QueryClient,
  QueryFunctionContext,
  QueryKey,
  QueryObserver,
  QueryObserverOptions,
  QueryObserverResult,
  WithRequired,
} from '@tanstack/query-core'
import { firstValueFrom, isObservable, Observable, shareReplay, Subject, takeUntil } from 'rxjs'

/**
 * Observable-shaped primitives over TanStack Query.
 *
 * TanStack's own Angular package is signals-only: `injectQuery` has to be called from an injection
 * context, reads its options through a reactive function, and wants a `queryFn` returning a Promise.
 * This app queries the other way round — a service builds a query inside a method from plain
 * arguments and hands back an Observable of the result, and every `queryFn` is an `HttpClient` call.
 *
 * These are that shape, over TanStack's own observers and the one `QueryClient` the app provides. A
 * screen is free to use `injectQuery` from `@tanstack/angular-query-experimental` directly instead;
 * both drive the same client, so the two share a cache and each other's invalidations.
 */

type ObservableQueryFunction<TQueryFnData, TQueryKey extends QueryKey, TPageParam = never> = (
  context: QueryFunctionContext<TQueryKey, TPageParam>
) => TQueryFnData | Promise<TQueryFnData> | Observable<TQueryFnData>

export type AppQueryOptions<TQueryFnData = unknown, TError = DefaultError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey> = Omit<
  WithRequired<QueryObserverOptions<TQueryFnData, TError, TData, TQueryFnData, TQueryKey>, 'queryKey'>,
  'queryFn'
> & {
  queryFn: ObservableQueryFunction<TQueryFnData, TQueryKey>
}

export type AppInfiniteQueryOptions<
  TQueryFnData = unknown,
  TError = DefaultError,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
> = Omit<WithRequired<InfiniteQueryObserverOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>, 'queryKey'>, 'queryFn'> & {
  queryFn: ObservableQueryFunction<TQueryFnData, TQueryKey, TPageParam>
}

export type AppMutationOptions<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown> = Omit<
  MutationObserverOptions<TData, TError, TVariables, TContext>,
  'mutationFn'
> & {
  mutationFn: (variables: TVariables) => Promise<TData> | Observable<TData>
}

export type AppQueryResult<TResult> = {
  result$: Observable<TResult>
}

export type AppMutationResult<TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown> = {
  mutate: (variables: TVariables, options?: MutateOptions<TData, TError, TVariables, TContext>) => void
  mutateAsync: (variables: TVariables, options?: MutateOptions<TData, TError, TVariables, TContext>) => Promise<TData>
  result$: Observable<MutationObserverResult<TData, TError, TVariables, TContext>>
  result: Signal<MutationObserverResult<TData, TError, TVariables, TContext>>
  /**
   * Detaches the observer. A caller that only ever uses `mutateAsync` never subscribes to `result$`,
   * so nothing else ever unsubscribes for it, and TanStack keeps a finished mutation — and whatever
   * it returned — in the cache for as long as an observer is attached.
   */
  reset: () => void
}

/**
 * A fetch is cancelled through the context's `AbortSignal` rather than by unsubscribing, so the
 * source completes without a value and the promise rejects — which is what TanStack reads as a
 * cancellation.
 */
function firstValueOrAbort<T>(source: Observable<T>, signal: AbortSignal | undefined): Promise<T> {
  if (signal === undefined) {
    return firstValueFrom(source)
  }
  const aborted = new Subject<void>()
  signal.addEventListener('abort', () => {
    aborted.next()
    aborted.complete()
  })
  return firstValueFrom(source.pipe(takeUntil(aborted)))
}

/** Turns an Observable-returning `queryFn` into the Promise-returning one TanStack expects. */
function toPromiseQueryFn<TOptions extends { queryFn?: unknown }>(options: TOptions): TOptions {
  const queryFn = options.queryFn
  // Anything that is not a function is TanStack's own `skipToken`, which has to reach it untouched.
  if (typeof queryFn !== 'function') {
    return options
  }
  return {
    ...options,
    queryFn(this: unknown, context: QueryFunctionContext<QueryKey, unknown>) {
      const value: unknown = (queryFn as (context: unknown) => unknown).call(this, context)
      return isObservable(value) ? firstValueOrAbort(value, context.signal) : value
    },
  }
}

/**
 * The app's query client. Its overrides let one options object — `queryFn` and all — be handed both
 * to `injectQuery` and to a prefetch, rather than one of the two needing a hand-written Promise.
 */
export class AppQueryClient extends QueryClient {
  override fetchQuery<TQueryFnData, TError = DefaultError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey, TPageParam = never>(
    options: AppQueryOptions<TQueryFnData, TError, TData, TQueryKey> | FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>
  ): Promise<TData> {
    return super.fetchQuery(toPromiseQueryFn(options) as FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>)
  }

  override prefetchQuery<TQueryFnData = unknown, TError = DefaultError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
    options: AppQueryOptions<TQueryFnData, TError, TData, TQueryKey> | FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>
  ): Promise<void> {
    return super.prefetchQuery(toPromiseQueryFn(options) as FetchQueryOptions<TQueryFnData, TError, TData, TQueryKey>)
  }

  override ensureQueryData<TQueryFnData, TError = DefaultError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
    options: AppQueryOptions<TQueryFnData, TError, TData, TQueryKey> | EnsureQueryDataOptions<TQueryFnData, TError, TData, TQueryKey>
  ): Promise<TData> {
    return super.ensureQueryData(toPromiseQueryFn(options) as EnsureQueryDataOptions<TQueryFnData, TError, TData, TQueryKey>)
  }

  override fetchInfiniteQuery<TQueryFnData, TError = DefaultError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey, TPageParam = unknown>(
    options:
      | AppInfiniteQueryOptions<TQueryFnData, TError, InfiniteData<TData, TPageParam>, TQueryKey, TPageParam>
      | FetchInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>
  ): Promise<InfiniteData<TData, TPageParam>> {
    return super.fetchInfiniteQuery(toPromiseQueryFn(options) as FetchInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>)
  }

  override prefetchInfiniteQuery<TQueryFnData, TError = DefaultError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey, TPageParam = unknown>(
    options:
      | AppInfiniteQueryOptions<TQueryFnData, TError, InfiniteData<TData, TPageParam>, TQueryKey, TPageParam>
      | FetchInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>
  ): Promise<void> {
    return super.prefetchInfiniteQuery(toPromiseQueryFn(options) as FetchInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>)
  }
}

/**
 * Provides the single query client every screen reads. `provideTanStackQuery` registers it under
 * TanStack's own `QueryClient` token and owns its mount/unmount; the alias is what lets
 * `injectQueryClient` hand back the Observable-aware overrides without a cast.
 */
export function provideAppQueryClient(client: AppQueryClient, ...features: QueryFeatures[]): EnvironmentProviders {
  return makeEnvironmentProviders([provideTanStackQuery(client, ...features), { provide: AppQueryClient, useExisting: QueryClient }])
}

export function injectQueryClient(): AppQueryClient {
  return inject(AppQueryClient)
}

// Both observers are driven identically and only their result type differs, which the two callers
// below restore. Naming it here instead would cost a cast at every use of `trackResult`, whose
// inherited signature still describes the non-infinite result.
type AnyQueryObserver = QueryObserver<any, any, any, any, any>

function createResult(client: QueryClient, createObserver: (options: any) => AnyQueryObserver, options: object): AppQueryResult<any> {
  const defaultedOptions = client.defaultQueryOptions(toPromiseQueryFn(options) as any)
  defaultedOptions._optimisticResults = 'optimistic'
  let observer: AnyQueryObserver | undefined

  const result$ = new Observable<any>((subscriber) => {
    // Created on the first subscription rather than up front, so a query nobody listens to is never
    // registered with the cache and never fetches.
    observer ??= createObserver(defaultedOptions)
    subscriber.next(observer.getOptimisticResult(defaultedOptions))
    const unsubscribe = observer.subscribe(
      notifyManager.batchCalls((result) => {
        // Tracking which properties a subscriber reads is what stops an untouched field moving —
        // `dataUpdatedAt`, say — from waking every consumer of the result.
        subscriber.next(observer?.trackResult(result) ?? result)
      })
    )
    return () => {
      unsubscribe()
      observer = undefined
    }
  }).pipe(shareReplay({ bufferSize: 1, refCount: true }))

  return { result$ }
}

function createMutationResult<TData, TError, TVariables, TContext>(
  client: QueryClient,
  injector: Injector,
  options: AppMutationOptions<TData, TError, TVariables, TContext>
): AppMutationResult<TData, TError, TVariables, TContext> {
  const observer = new MutationObserver<TData, TError, TVariables, TContext>(client, {
    ...options,
    mutationFn: (variables) => {
      const value = options.mutationFn(variables)
      return isObservable(value) ? firstValueFrom(value) : value
    },
  })

  const result$ = new Observable<MutationObserverResult<TData, TError, TVariables, TContext>>((subscriber) => {
    subscriber.next(observer.getCurrentResult())
    return observer.subscribe(notifyManager.batchCalls((result) => subscriber.next(result)))
  }).pipe(shareReplay({ bufferSize: 1, refCount: true }))

  let result: Signal<MutationObserverResult<TData, TError, TVariables, TContext>> | undefined
  return {
    // The rejection is reported through the result, so letting it also surface as an unhandled
    // rejection says nothing new. `mutateAsync` is the half that hands the caller the failure.
    mutate: (variables, mutateOptions) => void observer.mutate(variables, mutateOptions).catch(() => {}),
    mutateAsync: (variables, mutateOptions) => observer.mutate(variables, mutateOptions),
    result$,
    // Built on first read, which can happen anywhere — a callback, an event handler — so the
    // injector has to come from where `injectMutation` ran rather than from the ambient context.
    get result() {
      result ??= toSignal(result$, { requireSync: true, injector })
      return result
    },
    reset: () => observer.reset(),
  }
}

/**
 * Returns a function that builds a query and hands its result back as an Observable. Call this in an
 * injection context; the query itself can then be built anywhere, as often as needed. The same holds
 * for the two below.
 */
export function injectQuery() {
  const client = inject(QueryClient)
  return <TQueryFnData = unknown, TError = DefaultError, TData = TQueryFnData, TQueryKey extends QueryKey = QueryKey>(
    options: AppQueryOptions<TQueryFnData, TError, TData, TQueryKey>
  ): AppQueryResult<QueryObserverResult<TData, TError>> => createResult(client, (defaultedOptions) => new QueryObserver(client, defaultedOptions), options)
}

export function injectInfiniteQuery() {
  const client = inject(QueryClient)
  return <TQueryFnData = unknown, TError = DefaultError, TData = InfiniteData<TQueryFnData>, TQueryKey extends QueryKey = QueryKey, TPageParam = unknown>(
    options: AppInfiniteQueryOptions<TQueryFnData, TError, TData, TQueryKey, TPageParam>
  ): AppQueryResult<InfiniteQueryObserverResult<TData, TError>> =>
    createResult(client, (defaultedOptions) => new InfiniteQueryObserver(client, defaultedOptions), options)
}

export function injectMutation() {
  const client = inject(QueryClient)
  const injector = inject(Injector)
  return <TData = unknown, TError = DefaultError, TVariables = void, TContext = unknown>(
    options: AppMutationOptions<TData, TError, TVariables, TContext>
  ): AppMutationResult<TData, TError, TVariables, TContext> => createMutationResult(client, injector, options)
}
